import Header from "@components/Header/Header"
import { useNavigate } from "react-router"
import { JIDRCurrency } from "@components/CurrencyIcon/CurrencyIcon"
import { toCurrencyFormat } from "@lib/utils"
import ShareIcon from "@components/ShareIcon/ShareIcon"
import CopyIcon from "@components/CopyIcon/CopyIcon"
import { useTranslation } from "react-i18next"
import { generateReferralLink, shareUrl } from "@lib/telegram"
import {
    Container,
    ContentContainer,
    PaddingContainer,
    TitleContainer,
    TitleText,
    ReferralCodeCard,
    CardTitle,
    CodeContainer,
    CodeText,
    LinkContainer,
    LinkText,
    IconContainer,
    DescriptionText,
    UsageContainer,
    UsageBox,
    UsageText,
    BoldText,
    StatsContainer,
    StatColumn,
    StatLabel,
    CurrencyContainer,
    AmountText,
    VerticalDivider,
    ReferralLinkCard,
    LogoWrapper,
    LabelText,
    IconGroupContainer,
    BottomSection,
    JagadLogoSoft
} from "./ReferralCode.styles"
import useReferralReward from "@hooks/useReferralReward"
import LoadingModal from "@components/LoadingModal/LoadingModal"
import { useEffect } from "react"
import { GRAYSCALE_260, WHITE } from "@styles/colors"
import { setHeaderColor } from "@lib/telegram"
import MiniAppLogo from "@components/MiniAppLogo/MiniAppLogo"
import TelegramIcon from "@components/TelegramIcon/TelegramIcon"

const ReferralCode = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    useEffect(() => {
        setHeaderColor(WHITE)
    }, [])

    const { data: referralData, isLoading: referralLoading, error: referralError } = useReferralReward()

    // Centralize referral link generation
    const referralCode = referralData?.referral_code
    const referralLink = referralCode ? generateReferralLink(referralCode) : null

    const handleShareReferral = () => {
        if (referralLink) {
            shareUrl(referralLink)
        }
    }

    // Check if data is unavailable (error or no data after loading)
    const isDataUnavailable = !referralLoading && (referralError || !referralData)

    // Compute usage display with proper state handling
    const usageDisplay = isDataUnavailable
        ? '—/—'
        : `${referralData?.used ?? 0}/${referralData?.limit ?? 0}`

    // Compute earned amount with proper null/undefined checks
    const earnedAmount = isDataUnavailable
        ? '—'
        : referralData?.reward_amount != null
            ? toCurrencyFormat(referralData.reward_amount)
            : '—'

    // Compute remaining referrals (clamped to 0 minimum)
    const remainingText = isDataUnavailable
        ? '—'
        : (() => {
            const remaining = Math.max(0, (referralData?.limit ?? 0) - (referralData?.used ?? 0))
            return `${remaining} ${remaining === 1 ? t('referralCode.stats.personSingular') : t('referralCode.stats.personPlural')}`
        })()

    // Compute description with safe number checks
    const getDescription = () => {
        if (referralData?.description) {
            return referralData.description
        }

        const rewardAmount = referralData?.program_referrer_reward_amount
        const requiredAmount = referralData?.program_minimum_top_up_amount

        if (rewardAmount != null && requiredAmount != null && !isNaN(Number(rewardAmount)) && !isNaN(Number(requiredAmount))) {
            return t('referralCode.card.description', {
                rewardAmount: toCurrencyFormat(Number(rewardAmount)),
                requiredAmount: toCurrencyFormat(Number(requiredAmount))
            })
        }

        return t('referralCode.card.description', { rewardAmount: '—', requiredAmount: '—' })
    }

    return (
        <Container>
            <Header title={t('referralCode.header')} showBackButton $transparent={true} onBackClick={() => navigate('/settings')} />
            <LoadingModal visible={referralLoading} />
            <ContentContainer>
                <PaddingContainer>
                    <TitleContainer>
                        <TitleText>
                            {t('referralCode.title')}
                        </TitleText>
                        <DescriptionText>
                            {getDescription()}
                        </DescriptionText>
                    </TitleContainer>
                    <ReferralCodeCard>
                        <JagadLogoSoft />
                        <CardTitle>
                            {t('referralCode.card.title')}
                        </CardTitle>
                        <CodeContainer>
                            <CodeText>
                                {referralData?.referral_code || '—'}
                            </CodeText>
                            {referralData?.referral_code && <IconContainer $backgroundColor={`${GRAYSCALE_260}4D`}
                                onClick={() => navigator.clipboard.writeText(referralData?.referral_code)}
                            >
                                <CopyIcon />
                            </IconContainer>}
                        </CodeContainer>
                    </ReferralCodeCard>
                    <ReferralLinkCard>
                        <LogoWrapper>
                            <MiniAppLogo />
                            <TelegramIcon />
                        </LogoWrapper>
                        <LabelText>{t('referralCode.link.title')}</LabelText>
                        <LinkContainer>
                            <LinkText>
                                {referralLink || '—'}
                            </LinkText>
                            {referralLink && (
                                <IconGroupContainer>
                                    <IconContainer onClick={handleShareReferral}>
                                        <ShareIcon type={2} />
                                    </IconContainer>
                                    <IconContainer
                                        onClick={() => {
                                            if (referralLink) {
                                                navigator.clipboard.writeText(referralLink);
                                            }
                                        }}
                                    >
                                        <CopyIcon />
                                    </IconContainer>
                                </IconGroupContainer>
                            )}
                        </LinkContainer>
                        <LabelText>{t('referralCode.link.description')}</LabelText>
                    </ReferralLinkCard>
                </PaddingContainer>
                <BottomSection>
                    <UsageContainer>
                        <UsageBox>
                            <UsageText>
                                <BoldText>{usageDisplay} </BoldText>{t('referralCode.card.usageText')}
                            </UsageText>
                        </UsageBox>
                    </UsageContainer>
                    <StatsContainer>
                        {/* left */}
                        <StatColumn>
                            <StatLabel>
                                {t('referralCode.stats.amountEarned')}
                            </StatLabel>
                            <CurrencyContainer>
                                <JIDRCurrency />
                                <AmountText>
                                    {earnedAmount}
                                </AmountText>
                            </CurrencyContainer>
                        </StatColumn>
                        {/* Spacer */}
                        <VerticalDivider />
                        {/* right */}
                        <StatColumn>
                            <StatLabel>
                                {t('referralCode.stats.referralRemaining')}
                            </StatLabel>
                            <AmountText>
                                {remainingText}
                            </AmountText>
                        </StatColumn>
                    </StatsContainer>
                </BottomSection>
            </ContentContainer>
        </Container>
    )
}

export default ReferralCode